import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Switch from "rc-switch";
import {
    Button,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    Nav,
    Row,
    TabContent,
    TabPane
} from "reactstrap";

import classnames from "classnames";
import moment from "moment";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Colxx, Separator } from "../../../../components/common/CustomBootstrap";
import Rating from "../../../../components/common/Rating";
import { firestore, functions } from "../../../../helpers/Firebase";
import { CalculatePoints, Capitalize, createNotification, getAmountColor, getDescription, getTitle, getWalletAmountDisplay } from "../../../../helpers/Utils";

const Customer = ({
    customer,
    dismiss,
}) => {

    const [activeTab, setActiveTab] = useState(1)
    const [tabs] = useState([
        { name: 'Personal Details', key: 1 },
        { name: 'Customer Orders', key: 2 },
        { name: 'Customer Wallet', key: 3 },
    ])

    // Use wallet log from customer prop instead of fetching it
    const [wallet, setWallet] = useState(customer?.walletLog || [])
    const customerWallet = wallet
    const [withdrawingId, setWithdrawingId] = useState(null)

    React.useEffect(() => {
        setWallet(customer?.walletLog || [])
    }, [customer])

    // Mirrors mobile MyWallet.js: walk eligible credits in created-desc order
    // and only include while running balance (= totalCredit) can cover them.
    const refundableIds = React.useMemo(() => {
        const totals = CalculatePoints(JSON.parse(JSON.stringify(customerWallet || [])))
        let balance = totals?.totalCredit || 0
        const credits = (customerWallet || [])
            .filter(rec => rec?.type === 'credit' && !rec?.transfered && !rec?.pendingGatewayRefund)
            .sort((a, b) => b.created - a.created)
        const ids = []
        credits.forEach(rec => {
            const amt = Number(rec?.amount) || 0
            if (balance > 0 && balance >= amt) {
                ids.push(rec.id)
                balance -= amt
            }
        })
        return ids
    }, [customerWallet])

    const handleWithdraw = async (item) => {
        if (!refundableIds.includes(item.id) || item?.pendingGatewayRefund || withdrawingId) return
        // eslint-disable-next-line no-alert
        const ok = window.confirm('Are you sure you want to withdraw this amount? This process usually takes 7-15 working days.')
        if (!ok) return
        setWithdrawingId(item.id)
        try {
            const refund = functions().httpsCallable('Refund')
            const { data } = await refund({
                order_id: item.payment_ref,
                customer_id: customer?.id || customer?.key,
                log_id: item.id,
            })
            if (data?.success === 'queued') {
                createNotification(
                    'info',
                    data?.message || 'Refund will process within 24 hours. The customer will be notified when it lands on the card.',
                    'Refund queued'
                )
                setWallet(prev => prev.map(w => w.id === item.id ? { ...w, pendingGatewayRefund: true } : w))
                return
            }
            if (data?.error) {
                if (data.error === 'already_refunded') {
                    // Idempotent — treat as success
                    setWallet(prev => prev.map(w => w.id === item.id ? { ...w, transfered: moment().valueOf() } : w))
                    return
                }
                if (data.error === 'payment_not_refundable') {
                    createNotification('error', 'This payment can no longer be refunded. Please contact support.', 'Withdraw')
                    return
                }
                if (data.error === 'queue_failed') {
                    createNotification('error', 'Could not queue the refund. Please try again in a moment.', 'Withdraw')
                    return
                }
                createNotification('error', 'Error while processing your request', 'Withdraw')
                return
            }
            setWallet(prev => prev.map(w => w.id === item.id ? { ...w, transfered: moment().valueOf() } : w))
        } catch (err) {
            console.log('withdraw error', err)
            createNotification('error', 'Error while processing your request', 'Withdraw')
        } finally {
            setWithdrawingId(null)
        }
    }
    const MyStatusCell = (order) => {
        let { status, canceledBy, cancelReason } = order;

        const getStatusClassName = (status) => {
            const classNameMap = {
                new: 'pendingStatusfieldText',
                pending: 'pendingStatusfieldText',
                accepted: 'acceptedStatusfieldText',
                active: 'processStatusfieldText',
                completed: 'completedStatusfieldText',
                notRated: 'completedStatusfieldText',
                rejected: 'rejectedStatusfieldText',
                rated: 'ratedStatusfieldText',
                canceled: 'rejectedStatusfieldText',
            };
            return classNameMap[status] || '';
        };

        const renderStatusText = () => {
            if (status === 'new') {
                return 'Pending';
            }
            if (status === 'notRated') {
                return 'Completed';
            }
            if (status === 'rated') {
                return (
                    <div className='hoverRating'>
                        {Capitalize(status)}
                        <i style={{ color: '#4a4a4a', marginLeft: '4px' }} className="simple-icon-info" /><br />
                        <Rating total={5} rating={order?.rate} />
                        <div className='ratingComment2' style={{
                            width: '200px',
                            zIndex: 100000000
                        }}>
                            {order?.comment || 'No Comment'}
                            {order?.rate ? <div >Average Rate: {order?.rate}</div> : ''}
                            {order?.serviceRate ? <div className="d-flex align-items-center">Service Rate: <Rating total={5} rating={order?.serviceRate} style={{ margin: '4px 8px' }} /></div> : ''}
                            {order?.workerRate ? <div className="d-flex align-items-center">Worker Rate: <Rating total={5} rating={order?.workerRate} style={{ margin: '4px 8px' }} /></div> : ''}
                            {order?.arrivalTimingRate ? <div className="d-flex align-items-center">Arrival Timing Rate: <Rating total={5} rating={order?.arrivalTimingRate} style={{ margin: '4px 8px' }} /></div> : ''}
                        </div>
                    </div>
                );
            }
            if (status === 'canceled' || status === 'rejected') {
                return (
                    <div className='hoverCanceled'>
                        {Capitalize(status)}
                        <i style={{ color: '#4a4a4a', marginLeft: '4px' }} className="simple-icon-info" /><br />
                        <div className='canceledComment2' style={{
                            width: '200px',
                            zIndex: 100000000
                        }}>
                            {canceledBy && (
                                <div>
                                    {status === 'rejected' ? 'Rejected by:' : 'Canceled By:'} {canceledBy} <br />
                                    {cancelReason ? `Reason: ${cancelReason}` : 'No Comment'}
                                </div>
                            )}
                            {!canceledBy && 'No Comment'}
                        </div>
                    </div>
                );
            }
            return Capitalize(status);
        };

        const className = getStatusClassName(status);
        const statusText = renderStatusText();

        return <div className={className}>{statusText}</div>;
    }
    return (

        <Modal size="lg" backdrop="static" isOpen={customer} >

            <ModalBody>
                <h3>{'Customer Information'}</h3>

                <Row>
                    <Colxx xxs="12">
                        <Nav tabs className="separator-tabs ml-0 mb-5">
                            {
                                tabs.map(item => {
                                    return !item.hide && (
                                        <div
                                            style={{ cursor: 'pointer' }}
                                            key={item.key}
                                            className={classnames({
                                                active: activeTab === item.key,
                                                "nav-link": true
                                            })}
                                            onClick={() => {
                                                setActiveTab(item.key);
                                            }}>
                                            {item.name}
                                        </div>
                                    )
                                })
                            }
                        </Nav>

                        <TabContent activeTab={activeTab}>
                            <TabPane tabId={1}>

                                {[{
                                    key: 'personalDetails', title: 'Customer Details',
                                    data: [
                                        { key: 'name', title: 'Name', width: '6', type: 'text', data: customer?.name },
                                        { key: 'number', title: 'Mobile number', width: '6', type: 'text', data: customer?.number },
                                        { key: 'email', title: 'Email', width: '6', type: 'text', data: customer?.email }
                                    ]
                                },
                                {
                                    key: 'orderDetails', title: 'Orders Details',
                                    data: [
                                        { key: 'firstOrder', title: 'First Order', width: '6', type: 'text', data: moment(customer?.firstOrder).format('DD/MM/YYYY hh:mm A') },
                                        { key: 'lastOrder', title: 'Last Order', width: '6', type: 'text', data: moment(customer?.lastOrder).format('DD/MM/YYYY hh:mm A') },
                                        { key: 'totalOrders', title: 'Total Orders', width: '6', type: 'text', data: customer?.orders?.length },
                                        { key: 'totalAmount', title: 'Total Revenue', width: '6', type: 'text', data: `AED ${Number(customer?.revenue).toFixed(2)}` },
                                    ]
                                }
                                ].map((rec, recIndex) => {
                                    return rec?.data?.length ? (
                                        <Row key={recIndex}>
                                            <Colxx xxs="12" className="mt-3">
                                                <h4>{rec.title}</h4>
                                            </Colxx>
                                            {rec.data.map((item, index) => {
                                                return (

                                                    <Colxx xxs="12" md={item.width || '6'} key={index}>
                                                        <Label className="mb-0  text-semi-muted">{rec.key === 'location' ? `Address ${index + 1}` : item.title}:</Label>
                                                        <p className="font-weight-bold" id={item.id} style={{ fontSize: '0.8rem' }}>{(item?.data || '--')}</p>
                                                    </Colxx>
                                                )
                                            })}
                                        </Row>
                                    ) : ''
                                })}

                                <MarketingPreferences customer={customer} />

                            </TabPane>
                            <TabPane tabId={2}>
                                <PerfectScrollbar
                                    option={{ suppressScrollX: true, wheelPropagation: false }}
                                >
                                    <div className="p-4 pt-0" style={{ height: '500px' }}>
                                        {customer?.orders?.sort((a, b) => b.created - a.created)?.map((item, index) => {
                                            return (
                                                <>
                                                    <Row className={`d-flex align-items-center ${index !== 0 && "mt-4"}`}>

                                                        <Colxx xxs="2" style={{ fontWeight: '700' }}>Order Number</Colxx>
                                                        <Colxx xxs="2" style={{ fontWeight: '700' }}>Provider</Colxx>
                                                        <Colxx xxs="2" style={{ fontWeight: '700' }}>Service</Colxx>
                                                        <Colxx xxs="2" style={{ fontWeight: '700' }}>Appointment Date</Colxx>
                                                        <Colxx xxs="2" style={{ fontWeight: '700' }}>Status</Colxx>
                                                        <Colxx xxs="2" style={{ fontWeight: '700' }}>Amount</Colxx>
                                                    </Row>
                                                    <Row key={index} className="d-flex align-items-center">

                                                        <Colxx xxs="2">
                                                            <span>#{item?.orderNumber}</span>
                                                        </Colxx>
                                                        <Colxx xxs="2">
                                                            <span>{item?.sp_name}</span>
                                                        </Colxx>
                                                        <Colxx xxs="2">
                                                            <span>{item?.service_name}</span>
                                                        </Colxx>
                                                        <Colxx xxs="2">
                                                            <span>{moment(item?.bookingDate).format('DD/MM/YYYY hh:mm A')}</span>
                                                        </Colxx>
                                                        <Colxx xxs="2">
                                                            <MyStatusCell {...item} />
                                                        </Colxx>
                                                        <Colxx xxs="2">
                                                            <span>AED {item?.price?.totalPrice}</span>
                                                        </Colxx>

                                                    </Row>
                                                    <Separator className="mt-4" />
                                                </>
                                            )
                                        })}
                                    </div>
                                </PerfectScrollbar>
                            </TabPane>
                            <TabPane tabId={3}>

                                <Row className={'justify-content-center pr-4 pl-4'}>
                                    <Colxx xxs="3" className='d-flex flex-column justify-content-center align-items-start'>
                                        <div className="salesTitle">Total</div>
                                        <div className="salesValue">{'AED ' + CalculatePoints(customerWallet).total.toFixed(2)}</div>
                                    </Colxx>
                                    <Colxx xxs="3" className='d-flex flex-column justify-content-center align-items-start'>
                                        <div className="salesTitle">Total Credit</div>
                                        <div className="salesValue">{'AED ' + CalculatePoints(customerWallet).totalCredit.toFixed(2)}</div>
                                    </Colxx>
                                    <Colxx xxs="3" className='d-flex flex-column justify-content-center align-items-start'>
                                        <div className="salesTitle">Total Cashback</div>
                                        <div className="salesValue">{'AED ' + CalculatePoints(customerWallet).totalCashback.toFixed(2)}</div>
                                    </Colxx>
                                    <Colxx xxs="3" className='d-flex flex-column justify-content-center align-items-start'>
                                        <div className="salesTitle">Total Vouchers</div>
                                        <div className="salesValue">{'AED ' + CalculatePoints(customerWallet).totalVouchers.toFixed(2)}</div>
                                    </Colxx>
                                    {/* <Colxx xxs="3" className='d-flex flex-column justify-content-center align-items-start'>
                                        <div className="salesTitle">Total BOGO</div>
                                        <div className="salesValue">{(CalculatePoints(customerWallet).totalBogoCount || 0)}</div>
                                    </Colxx> */}
                                </Row>
                                <Separator className="mt-4" />

                                <PerfectScrollbar
                                    option={{ suppressScrollX: true, wheelPropagation: false, }}
                                >
                                    <div className="p-4" style={{ height: '500px' }}>
                                        {customerWallet?.sort((a, b) => b.created - a.created)?.filter(t => !t?.voided)?.map((item, index) => {
                                            const eligible = refundableIds.includes(item.id) && !item?.pendingGatewayRefund
                                            const isWithdrawing = withdrawingId === item.id
                                            return (
                                                <>
                                                    <Row className={`d-flex align-items-center ${index !== 0 && "mt-4"}`}>

                                                        <Colxx xxs="2" style={{ fontWeight: '700' }}>Type</Colxx>
                                                        <Colxx xxs="3" style={{ fontWeight: '700' }}>Description</Colxx>
                                                        <Colxx xxs="3" style={{ fontWeight: '700' }}>Created</Colxx>
                                                        <Colxx xxs="2" style={{ fontWeight: '700' }}>Amount</Colxx>
                                                        <Colxx xxs="2" style={{ fontWeight: '700' }}>Action</Colxx>
                                                    </Row>
                                                    <Row key={index} className="d-flex align-items-center">

                                                        <Colxx xxs="2">
                                                            <span>{getTitle(item)}</span>
                                                        </Colxx>
                                                        <Colxx xxs="3">
                                                            <span>{getDescription(item) || '--'}</span>
                                                        </Colxx>
                                                        <Colxx xxs="3">
                                                            <span>{moment(item?.created).format('DD/MM/YYYY hh:mm A')}</span>
                                                        </Colxx>
                                                        <Colxx xxs="2">
                                                            <span style={{ fontWeight: 'bold', color: getAmountColor(item) }}>{getWalletAmountDisplay(item)}</span>

                                                        </Colxx>
                                                        <Colxx xxs="2">
                                                            {eligible ? (
                                                                <Button
                                                                    color="primary"
                                                                    size="xs"
                                                                    disabled={isWithdrawing}
                                                                    onClick={() => handleWithdraw(item)}
                                                                >
                                                                    {isWithdrawing ? 'Withdrawing...' : 'Withdraw'}
                                                                </Button>
                                                            ) : item?.transfered ? (
                                                                <span style={{ color: '#2ecc71', fontSize: '0.75rem' }}>Transferred</span>
                                                            ) : item?.pendingGatewayRefund ? (
                                                                <span style={{ color: '#f59e0b', fontSize: '0.75rem' }}>Queued</span>
                                                            ) : (
                                                                <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>--</span>
                                                            )}
                                                        </Colxx>
                                                    </Row>
                                                    <Separator className="mt-4" />
                                                </>
                                            )
                                        })}
                                    </div>
                                </PerfectScrollbar>
                            </TabPane>


                        </TabContent>

                    </Colxx>
                </Row>

            </ModalBody>
            <ModalFooter>
                <Button
                    color="danger"
                    className="btn-shadow float-right"
                    size="lg"
                    onClick={() => dismiss()}>
                    Dismiss
                </Button>
            </ModalFooter>
        </Modal>
    );
};

// GDPR-style marketing suppression. Admins can toggle `marketingOptIn` on the
// user doc; the marketing pipeline reads this and skips suppressed users. Also
// records `marketingOptOutReason` + `marketingOptOutAt` for audit trail per
// plan §3.7. Stores the actioning admin's uid on the user doc as well.
const OPT_OUT_REASONS = [
    { value: 'user_request',   label: 'User requested' },
    { value: 'hard_bounce',    label: 'Hard bounce / invalid contact' },
    { value: 'compliance',     label: 'Compliance / legal' },
];

const MarketingPreferences = ({ customer }) => {
    // Default true: users who signed up before the field existed are considered opted-in.
    // Only an explicit `false` counts as suppression.
    const initialOptIn = customer?.marketingOptIn !== false;
    const [optIn, setOptIn] = useState(initialOptIn);
    const [reason, setReason] = useState(customer?.marketingOptOutReason || 'user_request');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const currentUser = useSelector(s => s.authUser?.user);

    // Sync when a different customer opens
    useEffect(() => {
        setOptIn(customer?.marketingOptIn !== false);
        setReason(customer?.marketingOptOutReason || 'user_request');
        setError(null);
    }, [customer?.uid, customer?.marketingOptIn, customer?.marketingOptOutReason]);

    const uid = customer?.uid || customer?.id || customer?.UID;
    if (!uid) return null;

    const persist = async (nextOptIn) => {
        setSaving(true);
        setError(null);
        try {
            const now = Date.now();
            const patch = {
                marketingOptIn: nextOptIn,
                marketingOptOutUpdatedAt: now,
                marketingOptOutUpdatedBy: currentUser?.uid || null,
                marketingOptOutUpdatedByName: currentUser?.displayName || currentUser?.email || null,
            };
            if (!nextOptIn) {
                patch.marketingOptOutReason = reason;
                patch.marketingOptOutAt = now;
            } else {
                // Clearing suppression: remove the reason + timestamp
                patch.marketingOptOutReason = null;
                patch.marketingOptOutAt = null;
            }
            await firestore().collection('users').doc(uid).set(patch, { merge: true });
            setOptIn(nextOptIn);
            createNotification('success', nextOptIn ? 'Marketing communications re-enabled' : 'Customer suppressed from marketing');
        } catch (e) {
            setError(e.message);
            createNotification('error', 'Failed to update: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    const suppressedAt = customer?.marketingOptOutAt
        ? moment(customer.marketingOptOutAt).format('DD/MM/YYYY hh:mm A')
        : null;
    const suppressedBy = customer?.marketingOptOutUpdatedByName || customer?.marketingOptOutUpdatedBy;

    return (
        <Row className="mt-3">
            <Colxx xxs="12">
                <h4 className="mb-2">Marketing preferences</h4>

                <div className="d-flex align-items-center" style={{ gap: 12 }}>
                    <Switch
                        className="custom-switch custom-switch-primary"
                        checked={optIn}
                        disabled={saving}
                        onChange={(checked) => persist(checked)}
                    />
                    <span>
                        <strong>{optIn ? 'Marketing allowed' : 'Suppressed from marketing'}</strong>
                    </span>
                    {saving && <small className="text-muted">Saving…</small>}
                </div>

                <p className="small text-muted mt-2 mb-0">
                    {optIn
                        ? 'This customer receives marketing pushes, campaigns, and automations. Turning this off skips them from every marketing send — transactional notifications (booking status, refunds, receipts) continue to reach them.'
                        : 'Automations, campaigns, and marketing pushes will skip this customer. Transactional notifications still fire.'}
                </p>

                {!optIn && (
                    <div className="mt-3" style={{ maxWidth: 360 }}>
                        <Label className="small text-semi-muted mb-1">Suppression reason</Label>
                        <Input
                            type="select"
                            bsSize="sm"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            disabled={saving}
                        >
                            {OPT_OUT_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </Input>
                        <Button
                            size="sm" color="secondary" outline
                            className="mt-2"
                            disabled={saving || reason === (customer?.marketingOptOutReason || 'user_request')}
                            onClick={() => persist(false)}
                        >
                            Update reason
                        </Button>
                    </div>
                )}

                {suppressedAt && !optIn && (
                    <div className="small text-muted mt-2">
                        Suppressed on {suppressedAt}{suppressedBy ? ` by ${suppressedBy}` : ''}.
                    </div>
                )}
                {error && <div className="small text-danger mt-1">{error}</div>}
            </Colxx>
        </Row>
    );
};

export default Customer;